import { useEffect, useState } from 'react';
import { Check, TriangleAlert, UserRound } from 'lucide-react';
import {
  useListStaffQuery,
  useSetGroupStaffMutation,
  getApiError,
} from '@/store/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  ═════════════════════════════════════════════════════════════════════════════
  WHO TEACHES THIS CLASS

  Written from the class's side, which is how somebody staffing a school
  actually thinks — "who is taking Class 3" rather than "which classes does
  Efua take". The storage is unchanged: it is still the `groupIds` on each
  teacher, so every rule already scoped by it — the register, mark entry, a
  teacher's own exams — keeps working with nothing to re-wire.

  Why only teachers appear
  ────────────────────────
  Being on a class is what GIVES a teacher their reach, so it means something
  for exactly one role. A gate officer works the gate, and an administrator
  already reaches every class — putting either on one would imply a permission
  neither takes from it.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function GroupStaffDialog({
  open,
  onClose,
  institutionId,
  group,
}: {
  open: boolean;
  onClose: () => void;
  institutionId: string;
  group: { _id: string; name: string; staff: Array<{ id: string }> } | null;
}) {
  const { data: staff = [] } = useListStaffQuery(institutionId, { skip: !institutionId || !open });
  const [setGroupStaff, saveState] = useSetGroupStaffMutation();
  const [picked, setPicked] = useState<string[]>([]);

  const teachers = staff.filter((s) => s.role === 'teacher' && s.isActive);

  useEffect(() => {
    if (open && group) setPicked(group.staff.map((s) => s.id));
  }, [open, group]);

  const toggle = (id: string) =>
    setPicked((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={group ? `Who teaches ${group.name}` : 'Class staff'}
      description="They can take this class's register, enter its marks, and see it on the dismissal board. Nothing else."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={saveState.isLoading}
            onClick={() =>
              group &&
              void setGroupStaff({ institutionId, groupId: group._id, userIds: picked }).then(() => onClose())
            }
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {teachers.length === 0 ? (
          <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/[0.08] px-3.5 py-3">
            <TriangleAlert size={15} className="mt-[1px] flex-shrink-0 text-[hsl(var(--record-ink-warning))]" />
            <p className="text-[11.5px] leading-relaxed text-[hsl(var(--record-ink-warning))]">
              No active teachers yet. Add one under Staff — a gate officer or an administrator cannot be put
              on a class, because being on one is what gives a teacher their reach.
            </p>
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-border overflow-clip rounded-md border border-border">
            {teachers.map((t) => {
              const on = picked.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggle(t.id)}
                  aria-pressed={on}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 text-left transition-colors',
                    on ? 'bg-primary/[0.07]' : 'hover:bg-muted',
                  )}
                >
                  <Avatar src={t.photoUrl} name={t.fullName} size={28} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12.5px] font-semibold text-foreground">{t.fullName}</p>
                    <p className={cn(styles.meta, 'truncate')}>{t.email}</p>
                  </div>
                  <span
                    className={cn(
                      'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border',
                      on ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                    )}
                  >
                    {on && <Check size={13} />}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <p className={styles.meta}>
          <UserRound size={11} className="mr-1 inline" />
          A class can have several teachers, and a teacher can take several classes.
        </p>

        {saveState.error && (
          <p role="alert" className="text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]">
            {getApiError(saveState.error)}
          </p>
        )}
      </div>
    </Modal>
  );
}
